import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    type AllWeeklyReportResult,
    useGetLiceReportWeeks,
    useGetSelectedWeeklyLiceReports,
} from '@/domain/liceReport';
import type { LiceReportWeekNumber } from '@/routes/Client/LiceReport';

import { defaultRegionLiceReportWeek } from './defaultRegionLiceReportWeek';

interface WeekChoice {
    isoWeek: number;
    isoYear: number;
}

/**
 * Which weeks the region report fetches.
 *
 * The selector still lists every week. The page opens on the newest one that has rows, so an
 * empty running week is fetched, skipped, and replaced by the newest saved report.
 */
export interface UseRegionLiceReportWeeksResult {
    isLoadingWeekly: boolean;
    weeklyError: unknown;
    weeklyQuery: {
        error: unknown;
        isFetching: boolean;
        refetch: () => void;
    };
    reports: AllWeeklyReportResult[];
    weeklyReports: AllWeeklyReportResult[];
    hasWeeks: boolean;
    handleWeeklySelectionChange: (weekNumbers: LiceReportWeekNumber[]) => void;
    defaultWeekKey: string | undefined;
}

export function useRegionLiceReportWeeks(clientId: number): UseRegionLiceReportWeeksResult {
    const [selectedWeeks, setSelectedWeeks] = useState<WeekChoice[] | null>(null);
    const [selectionSource, setSelectionSource] = useState<'auto' | 'user'>('auto');
    const [skippedWeekKeys, setSkippedWeekKeys] = useState<ReadonlySet<string>>(() => new Set());
    const [skippedForClient, setSkippedForClient] = useState(clientId);
    // The page stays mounted while the reader moves between clients. A week skipped or chosen
    // for one client must not hide the week the next client would have opened on.
    if (skippedForClient !== clientId) {
        setSkippedForClient(clientId);
        setSkippedWeekKeys(new Set());
        setSelectedWeeks(null);
        setSelectionSource('auto');
    }

    const weeksQuery = useGetLiceReportWeeks(clientId);
    const autoWeek = useMemo(
        () => defaultRegionLiceReportWeek(weeksQuery.data, skippedWeekKeys),
        [skippedWeekKeys, weeksQuery.data]
    );
    const autoWeekRef = useRef(autoWeek);
    autoWeekRef.current = autoWeek;
    const weeksToFetch = useMemo(() => {
        if (selectionSource === 'user' && selectedWeeks && selectedWeeks.length > 0) {
            return selectedWeeks;
        }
        return autoWeek ? [{ isoWeek: autoWeek.isoWeek, isoYear: autoWeek.isoYear }] : [];
    }, [autoWeek, selectedWeeks, selectionSource]);
    const selectedWeeksQuery = useGetSelectedWeeklyLiceReports(clientId, weeksToFetch);
    const autoReport =
        autoWeek === undefined
            ? undefined
            : selectedWeeksQuery.data.find(
                  (report) =>
                      report.isoWeek === autoWeek.isoWeek && report.isoYear === autoWeek.isoYear
              );
    // Hold the skeleton until an empty automatic week has been replaced by one that has rows.
    const autoReportEmpty =
        selectionSource === 'auto' && autoReport !== undefined && autoReport.locations.length === 0;

    useEffect(() => {
        if (!autoReportEmpty || autoWeek === undefined) {
            return;
        }
        const key = `${autoWeek.isoWeek}-${autoWeek.isoYear}`;
        setSkippedWeekKeys((current) => {
            if (current.has(key)) {
                return current;
            }
            const next = new Set(current);
            next.add(key);
            return next;
        });
    }, [autoReportEmpty, autoWeek]);

    const handleWeeklySelectionChange = useCallback((weekNumbers: LiceReportWeekNumber[]) => {
        const next = weekNumbers.map((week) => ({
            isoWeek: week.weekNumber,
            isoYear: week.year,
        }));
        if (next.length === 0) {
            setSelectionSource('auto');
            setSelectedWeeks(null);
            return;
        }
        const automatic = autoWeekRef.current;
        const followsAutomatic =
            automatic !== undefined &&
            next.length === 1 &&
            next[0]?.isoWeek === automatic.isoWeek &&
            next[0]?.isoYear === automatic.isoYear;
        setSelectionSource(followsAutomatic ? 'auto' : 'user');
        setSelectedWeeks((current) => {
            if (
                current !== null &&
                current.length === next.length &&
                current.every(
                    (week, index) =>
                        week.isoWeek === next[index]?.isoWeek &&
                        week.isoYear === next[index]?.isoYear
                )
            ) {
                return current;
            }
            return next;
        });
    }, []);

    const weeklyReports = useMemo(
        () =>
            weeksQuery.data.map((week) => ({
                isoWeek: week.isoWeek,
                isoYear: week.isoYear,
                interimReport: week.interimReport,
                locations: [],
            })),
        [weeksQuery.data]
    );

    return {
        isLoadingWeekly:
            weeksQuery.isLoading ||
            (selectedWeeksQuery.isLoading && selectedWeeksQuery.data.length === 0) ||
            autoReportEmpty,
        weeklyError: weeksQuery.error ?? selectedWeeksQuery.error,
        weeklyQuery: {
            error: weeksQuery.error ?? selectedWeeksQuery.error,
            isFetching: weeksQuery.isFetching || selectedWeeksQuery.isFetching,
            refetch: () => {
                void weeksQuery.refetch();
                selectedWeeksQuery.refetch();
            },
        },
        reports: autoReportEmpty ? [] : selectedWeeksQuery.data,
        weeklyReports,
        hasWeeks: weeksQuery.data.length > 0,
        handleWeeklySelectionChange,
        defaultWeekKey: autoWeek ? `${autoWeek.isoWeek}-${autoWeek.isoYear}` : undefined,
    };
}
